// Client-side registration of the two check-reminder widgets (IM-027, sprint 7).
//
// A dashboards widget becomes renderable by handing a factory to the library's widget registry,
// published as `globalThis.SisolcoDashboards.registerWidget(key, factory)` as a side effect of the
// catalogue barrel. The full account of why the global is used and the module URL deliberately is
// not — the catalogue chunk is ~1.5 MB and would be downloaded on every page in the application to
// register a widget that will never be asked for — is at the top of
// `Modules/Common/Process/processPortfolioRegistration.ts`. This file follows that precedent exactly.
//
// ⚠️⚠️ THE ORDERING IS THE WHOLE PROBLEM, AND A NAIVE READ OF THE GLOBAL SILENTLY FAILS. `ScriptInit`
// is a module in `_LayoutHead.cshtml`; the dashboards bootstrap is a module in the page body. Module
// scripts are deferred and execute in document order, so ScriptInit runs FIRST — before the catalogue
// has loaded and therefore before anything has been put on the global. The failure is invisible: no
// error, no console line, just a card that draws its title and nothing else. So registration is
// DEFERRED to `DOMContentLoaded` when there is no registrar yet, which is still ahead of the first
// cell because `mountDashboardByKey` awaits an HTTP retrieve of the layout before creating one.

import {
    CheckReminderWidget,
    MyChecksDueWidgetKey,
    ProjectChecksDueWidgetKey,
} from "./checkReminderWidget";

/** The registry surface the library publishes on its global. */
export type WidgetRegistrar = (key: string, factory: () => unknown) => void;

interface DashboardsGlobal {
    registerWidget?: WidgetRegistrar;
}

/** What a registration attempt did, so a caller (and a test) can tell the three cases apart. */
export type RegistrationOutcome =
    /** A registrar was there and both factories were handed over. */
    | "registered"
    /** No registrar yet, but the document is still parsing — retried at DOMContentLoaded. */
    | "deferred"
    /** No registrar, and no later point at which one is expected. Not an error. */
    | "unavailable";

/** Both keys this module registers, in the order it registers them. */
export const CheckReminderWidgetKeys = [MyChecksDueWidgetKey, ProjectChecksDueWidgetKey] as const;

/**
 * The registrar, if the host page has one. Reads a global and nothing else — no network, no import,
 * no console output — so calling it on every page costs nothing and says nothing when absent.
 */
export function resolveWidgetRegistrar(scope: unknown = globalThis): WidgetRegistrar | undefined {
    const dashboards = (scope as { SisolcoDashboards?: DashboardsGlobal } | null | undefined)
        ?.SisolcoDashboards;

    return typeof dashboards?.registerWidget === "function"
        ? dashboards.registerWidget.bind(dashboards)
        : undefined;
}

/**
 * Registers both widget factories when there is somewhere to register them, right now.
 *
 * Returns whether it did, so a caller can tell "registered" from "no registrar on this page yet"
 * without either being an error. It is not an error: most pages host no dashboard at all.
 */
export function registerCheckReminderWidgets(scope: unknown = globalThis): boolean {
    const register = resolveWidgetRegistrar(scope);
    if (!register) return false;

    // ⚠️ A factory per key, each closing over its own key. One shared instance would give both cells
    // the same widget object, and a dashboard carrying both would have the second render overwrite
    // the first's host element.
    for (const key of CheckReminderWidgetKeys)
        register(key, () => new CheckReminderWidget(key));

    return true;
}

/**
 * Registers the widgets, waiting for the dashboards runtime to publish its registry if it has not
 * done so yet. This is what `ScriptInit` calls; see the ordering note at the top of the file for why
 * the plain synchronous call is not enough.
 *
 * `doc` is a parameter so the deferral can be driven in a test rather than only observed.
 */
export function installCheckReminderWidgetRegistration(
    scope: unknown = globalThis,
    doc: Pick<Document, "readyState" | "addEventListener"> | undefined =
        typeof document === "undefined" ? undefined : document,
): RegistrationOutcome {
    if (registerCheckReminderWidgets(scope)) return "registered";

    // ⚠️ The test is "not complete", NOT "loading". A deferred module script — which is what this is
    // — executes AFTER parsing finishes, so `readyState` reads "interactive" here, never "loading".
    // DOMContentLoaded has still not fired at that point, so it is a real later point and the only
    // one worth taking. Guarding on "loading" alone never defers, and the widgets are left
    // unregistered on every page in the application.
    if (doc && doc.readyState !== "complete") {
        doc.addEventListener("DOMContentLoaded", () => { registerCheckReminderWidgets(scope); },
            { once: true });
        return "deferred";
    }

    return "unavailable";
}
